<template>
  <menu-layout :loading="loading" :title="$t('reports.title')" :subtitle="$t('reports.subtitle')">
    <row>
      <col12>
        <v-btn color="primary" class="mb-4" @click="openCreate" data-testid="report-create-btn">
          {{ $t('reports.btn_create') }}
        </v-btn>

        <v-alert
          v-if="!loading && reports.length === 0"
          type="info"
          variant="tonal"
          class="mb-4"
          density="comfortable"
        >
          {{ $t('reports.empty') }}
        </v-alert>

        <v-expansion-panels v-if="reports.length > 0" multiple>
          <v-expansion-panel
            v-for="report in reports"
            :key="report.id"
            :data-testid="'report-panel-' + report.id"
          >
            <v-expansion-panel-title>
              <div class="d-flex align-center flex-wrap ga-2 w-100">
                <span class="font-weight-medium">{{ report.name }}</span>
                <v-chip
                  v-if="isMonthly(report)"
                  size="small"
                  variant="outlined"
                  data-testid="report-monthly-badge"
                >
                  {{ $t('reports.type_monthly') }}
                </v-chip>
                <v-chip size="small" :color="statusColor(report)" variant="tonal">
                  {{ statusLabel(report) }}
                </v-chip>
                <span v-if="report.calculated_at" class="text-caption text-medium-emphasis">
                  {{ formatDate(report.calculated_at) }}
                </span>
              </div>
            </v-expansion-panel-title>
            <v-expansion-panel-text>
              <div v-if="report.error_message" class="text-error text-caption mb-2">
                {{ report.error_message }}
              </div>

              <template v-if="isMonthly(report)">
                <div
                  class="d-flex align-center justify-space-between flex-wrap ga-2 mb-3"
                >
                  <v-btn-toggle
                    :model-value="viewMode(report)"
                    mandatory
                    density="compact"
                    color="primary"
                    divided
                    data-testid="report-view-mode"
                    @update:model-value="(mode) => setViewMode(report, mode)"
                  >
                    <v-btn value="all" size="small" data-testid="report-view-mode-all">
                      {{ $t('reports.view_all') }}
                    </v-btn>
                    <v-btn value="year" size="small" data-testid="report-view-mode-year">
                      {{ $t('reports.view_by_year') }}
                    </v-btn>
                  </v-btn-toggle>

                  <div
                    v-if="viewMode(report) === 'year'"
                    class="d-flex align-center justify-space-between flex-grow-1"
                    style="min-width: 240px; max-width: 420px; margin-left: auto;"
                    data-testid="report-year-nav"
                  >
                    <v-btn
                      variant="text"
                      color="primary"
                      :disabled="!prevYear(report)"
                      @click="setViewYear(report, prevYear(report))"
                      data-testid="report-year-prev"
                    >
                      <v-icon start size="small">fas fa-arrow-left</v-icon>
                      {{ prevYear(report) || '—' }}
                    </v-btn>
                    <span class="text-h6" data-testid="report-year-current">
                      {{ viewYear(report) }}
                    </span>
                    <v-btn
                      variant="text"
                      color="primary"
                      :disabled="!nextYear(report)"
                      @click="setViewYear(report, nextYear(report))"
                      data-testid="report-year-next"
                    >
                      {{ nextYear(report) || '—' }}
                      <v-icon end size="small">fas fa-arrow-right</v-icon>
                    </v-btn>
                  </div>
                </div>

                <div class="report-monthly-table-wrap mb-3">
                  <v-table density="comfortable" class="report-monthly-table">
                    <thead>
                      <tr>
                        <th>{{ $t('reports.col_label') }}</th>
                        <th>{{ $t('reports.col_variable') }}</th>
                        <th
                          v-for="periodKey in periodKeys(report)"
                          :key="periodKey"
                          class="text-right"
                        >
                          {{ periodLabel(report, periodKey) }}
                        </th>
                        <th class="text-right">{{ $t('reports.col_total') }}</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr
                        v-for="row in reportRows(report)"
                        :key="row.id"
                        :data-testid="'report-value-' + row.id"
                      >
                        <td>{{ row.label }}</td>
                        <td><code>{{ row.id }}</code></td>
                        <td
                          v-for="periodKey in periodKeys(report)"
                          :key="periodKey"
                          class="text-right"
                        >
                          {{ formatPeriodValue(report, row.id, periodKey) }}
                        </td>
                        <td class="text-right font-weight-medium">
                          {{ formatPeriodTotal(report, row.id) }}
                        </td>
                      </tr>
                    </tbody>
                  </v-table>
                </div>

                <monthly-report-graph
                  v-if="report.status === 'ready'"
                  :rows="reportRows(report)"
                  :period-keys="periodKeys(report)"
                  :values-by-row="graphValuesByRow(report)"
                />
              </template>

              <v-table v-else density="comfortable" class="mb-3">
                <thead>
                  <tr>
                    <th>{{ $t('reports.col_label') }}</th>
                    <th>{{ $t('reports.col_variable') }}</th>
                    <th class="text-right">{{ $t('reports.col_value') }}</th>
                  </tr>
                </thead>
                <tbody>
                  <tr
                    v-for="row in reportRows(report)"
                    :key="row.id"
                    :data-testid="'report-value-' + row.id"
                  >
                    <td>{{ row.label }}</td>
                    <td><code>{{ row.id }}</code></td>
                    <td class="text-right">{{ formatValue(report, row.id) }}</td>
                  </tr>
                </tbody>
              </v-table>

              <v-btn
                size="small"
                variant="outlined"
                class="mr-2"
                :loading="refreshingId === report.id"
                @click="refreshReport(report)"
                data-testid="report-refresh-btn"
              >
                {{ $t('reports.btn_refresh') }}
              </v-btn>
              <v-btn size="small" variant="text" @click="openEdit(report)">
                {{ $t('reports.btn_edit') }}
              </v-btn>
              <v-btn size="small" variant="text" color="error" @click="deleteReport(report)">
                {{ $t('reports.btn_delete') }}
              </v-btn>
            </v-expansion-panel-text>
          </v-expansion-panel>
        </v-expansion-panels>

        <v-btn class="mt-4" variant="outlined" @click="load">
          {{ $t('reports.btn_reload') }}
        </v-btn>
      </col12>
    </row>

    <edit-report-dialog
      v-if="showDialog"
      :report="editingReport"
      @close="closeDialog"
      @saved="onSaved"
    />
  </menu-layout>
</template>

<script>
import dayjs from 'dayjs';
import MenuLayout from '@/components/layout/MenuLayout';
import Row from '@/components/layout/Row';
import Col12 from '@/components/layout/Col12';
import EditReportDialog from '@/components/reports/EditReportDialog';
import MonthlyReportGraph from '@/components/reports/MonthlyReportGraph';

const MONTH_SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

export default {
  name: 'Reports',
  components: { MenuLayout, Row, Col12, EditReportDialog, MonthlyReportGraph },
  data() {
    return {
      loading: false,
      reports: [],
      showDialog: false,
      editingReport: null,
      refreshingId: null,
      pollTimer: null,
      viewYears: {},
      viewModes: {},
    };
  },
  mounted() {
    this.load();
  },
  beforeUnmount() {
    this.stopPolling();
  },
  methods: {
    async load() {
      this.loading = true;
      try {
        const res = await this.$api.report.list();
        this.reports = res?.items || [];
        this.syncPolling();
        this.ensureViewYears();
      } catch (e) {
        console.error('Load reports failed', e);
      } finally {
        this.loading = false;
      }
    },
    ensureViewYears() {
      for (const report of this.reports) {
        if (!this.isMonthly(report)) {
          continue;
        }
        if (this.viewYears[report.id] != null) {
          continue;
        }
        const years = this.yearsForReport(report);
        const current = new Date().getFullYear();
        this.viewYears[report.id] = years.includes(current)
          ? current
          : (years[years.length - 1] || current);
      }
    },
    syncPolling() {
      const pending = this.reports.some((r) => r.status === 'queued' || r.status === 'calculating');
      if (pending && !this.pollTimer) {
        this.pollTimer = setInterval(() => this.load(), 3000);
      } else if (!pending) {
        this.stopPolling();
      }
    },
    stopPolling() {
      if (this.pollTimer) {
        clearInterval(this.pollTimer);
        this.pollTimer = null;
      }
    },
    openCreate() {
      this.editingReport = null;
      this.showDialog = true;
    },
    openEdit(report) {
      this.editingReport = report;
      this.showDialog = true;
    },
    closeDialog() {
      this.showDialog = false;
      this.editingReport = null;
    },
    async onSaved() {
      await this.load();
    },
    async refreshReport(report) {
      this.refreshingId = report.id;
      try {
        await this.$api.report.refresh(report.id);
        await this.load();
      } finally {
        this.refreshingId = null;
      }
    },
    async deleteReport(report) {
      if (!window.confirm(this.$t('reports.confirm_delete'))) {
        return;
      }
      await this.$api.report.delete(report.id);
      await this.load();
    },
    isMonthly(report) {
      const def = report && report.definition;
      return def && def.type === 'monthly';
    },
    allReportRows(report) {
      const def = report && report.definition;
      const items = def && def.items;
      return Array.isArray(items) ? items : [];
    },
    itemIsShown(item) {
      return item == null || item.show !== false;
    },
    reportRows(report) {
      return this.allReportRows(report).filter((item) => this.itemIsShown(item));
    },
    monthlyMonths(report) {
      const results = report && report.results;
      if (results && Array.isArray(results.months)) {
        return results.months;
      }
      return [];
    },
    yearsForReport(report) {
      const years = new Set();
      for (const key of this.monthlyMonths(report)) {
        const y = parseInt(String(key).slice(0, 4), 10);
        if (!Number.isNaN(y)) {
          years.add(y);
        }
      }
      // Fall back to definition range while calculating.
      if (years.size === 0 && report.definition) {
        const start = report.definition.start_date;
        const end = report.definition.end_date || dayjs().format('YYYY-MM-DD');
        if (start) {
          let y = parseInt(String(start).slice(0, 4), 10);
          const endY = parseInt(String(end).slice(0, 4), 10);
          while (!Number.isNaN(y) && !Number.isNaN(endY) && y <= endY) {
            years.add(y);
            y += 1;
          }
        }
      }
      return Array.from(years).sort((a, b) => a - b);
    },
    viewMode(report) {
      return this.viewModes[report.id] || 'all';
    },
    setViewMode(report, mode) {
      if (mode !== 'all' && mode !== 'year') {
        return;
      }
      this.viewModes = { ...this.viewModes, [report.id]: mode };
    },
    viewYear(report) {
      if (this.viewYears[report.id] != null) {
        return this.viewYears[report.id];
      }
      const years = this.yearsForReport(report);
      return years[years.length - 1] || new Date().getFullYear();
    },
    setViewYear(report, year) {
      if (year == null) {
        return;
      }
      this.viewYears = { ...this.viewYears, [report.id]: year };
    },
    prevYear(report) {
      const years = this.yearsForReport(report);
      const cur = this.viewYear(report);
      const idx = years.indexOf(cur);
      return idx > 0 ? years[idx - 1] : null;
    },
    nextYear(report) {
      const years = this.yearsForReport(report);
      const cur = this.viewYear(report);
      const idx = years.indexOf(cur);
      return idx >= 0 && idx < years.length - 1 ? years[idx + 1] : null;
    },
    monthsForYear(report) {
      const year = this.viewYear(report);
      const prefix = `${year}-`;
      const months = this.monthlyMonths(report).filter((m) => String(m).startsWith(prefix));
      if (months.length > 0) {
        return months;
      }
      // Placeholder columns while calculating.
      return Array.from({ length: 12 }, (_, i) => `${year}-${String(i + 1).padStart(2, '0')}`);
    },
    allMonths(report) {
      const months = this.monthlyMonths(report);
      if (months.length > 0) {
        return [...months].sort();
      }
      // Placeholder columns from definition range while calculating.
      if (report.definition && report.definition.start_date) {
        const start = dayjs(report.definition.start_date).startOf('month');
        const end = dayjs(report.definition.end_date || undefined).startOf('month');
        const out = [];
        let cur = start;
        while (cur.isBefore(end) || cur.isSame(end, 'month')) {
          out.push(cur.format('YYYY-MM'));
          cur = cur.add(1, 'month');
          if (out.length > 240) {
            break;
          }
        }
        if (out.length > 0) {
          return out;
        }
      }
      return this.monthsForYear(report);
    },
    periodKeys(report) {
      if (this.viewMode(report) === 'year') {
        return this.monthsForYear(report);
      }
      return this.allMonths(report);
    },
    periodLabel(report, periodKey) {
      if (this.viewMode(report) === 'all') {
        return this.monthYearLabel(periodKey);
      }
      return this.monthShortLabel(periodKey);
    },
    monthShortLabel(monthKey) {
      const parts = String(monthKey).split('-');
      const monthNum = parseInt(parts[1], 10);
      if (monthNum >= 1 && monthNum <= 12) {
        return MONTH_SHORT[monthNum - 1];
      }
      return monthKey;
    },
    monthYearLabel(monthKey) {
      const parts = String(monthKey).split('-');
      const year = parts[0];
      const monthNum = parseInt(parts[1], 10);
      if (monthNum >= 1 && monthNum <= 12 && year) {
        return `${MONTH_SHORT[monthNum - 1]} ${year}`;
      }
      return monthKey;
    },
    monthsForPeriod(_report, periodKey) {
      // Period keys are always month keys (YYYY-MM), in both All and Year views.
      return [periodKey];
    },
    rawPeriodValues(report) {
      if (report.status !== 'ready' || !report.results || !report.results.values) {
        return null;
      }
      const rows = this.allReportRows(report);
      const periods = this.periodKeys(report);
      const byRow = {};
      for (const periodKey of periods) {
        const monthKeys = this.monthsForPeriod(report, periodKey);
        const periodVals = {};
        for (const row of rows) {
          if (row.formula) {
            continue;
          }
          let sum = 0;
          let has = false;
          const byMonth = report.results.values[row.id] || {};
          for (const monthKey of monthKeys) {
            const v = byMonth[monthKey];
            if (v != null) {
              sum += Number(v);
              has = true;
            }
          }
          periodVals[row.id] = has ? sum : null;
        }
        for (const row of rows) {
          if (!row.formula) {
            continue;
          }
          try {
            periodVals[row.id] = this.evalFormula(row.formula, periodVals);
          } catch (e) {
            periodVals[row.id] = null;
          }
        }
        for (const row of rows) {
          if (!byRow[row.id]) {
            byRow[row.id] = {};
          }
          byRow[row.id][periodKey] = periodVals[row.id];
        }
      }
      return byRow;
    },
    formatNumber(val) {
      if (val == null) {
        return '-';
      }
      if (Number.isInteger(val)) {
        return String(val);
      }
      return Number(val).toLocaleString(undefined, { maximumFractionDigits: 2 });
    },
    formatValue(report, id) {
      if (report.status !== 'ready' || !report.results) {
        return '-';
      }
      return this.formatNumber(report.results[id]);
    },
    formatPeriodValue(report, id, periodKey) {
      const byRow = this.rawPeriodValues(report);
      if (!byRow || !byRow[id] || byRow[id][periodKey] == null) {
        return '-';
      }
      return this.formatNumber(byRow[id][periodKey]);
    },
    formatPeriodTotal(report, id) {
      const byRow = this.rawPeriodValues(report);
      if (!byRow) {
        return '-';
      }
      const rows = this.allReportRows(report);
      const periods = this.periodKeys(report);
      const totals = {};
      for (const row of rows) {
        if (row.formula) {
          continue;
        }
        let sum = 0;
        let has = false;
        const vals = byRow[row.id] || {};
        for (const periodKey of periods) {
          const v = vals[periodKey];
          if (v != null) {
            sum += Number(v);
            has = true;
          }
        }
        totals[row.id] = has ? sum : null;
      }
      for (const row of rows) {
        if (!row.formula) {
          continue;
        }
        try {
          totals[row.id] = this.evalFormula(row.formula, totals);
        } catch (e) {
          totals[row.id] = null;
        }
      }
      return this.formatNumber(totals[id]);
    },
    graphValuesByRow(report) {
      const byRow = this.rawPeriodValues(report);
      const periods = this.periodKeys(report);
      const out = {};
      for (const row of this.reportRows(report)) {
        const vals = (byRow && byRow[row.id]) || {};
        out[row.id] = periods.map((key) => {
          const v = vals[key];
          return v == null ? 0 : Number(v);
        });
      }
      return out;
    },
    evalFormula(formula, values) {
      const refs = String(formula).match(/[a-zA-Z_][a-zA-Z0-9_]*/g) || [];
      let expr = formula;
      for (const ref of refs) {
        if (!(ref in values) || values[ref] == null) {
          throw new Error(`unknown ${ref}`);
        }
        expr = expr.replace(new RegExp(`\\b${ref}\\b`, 'g'), `(${values[ref]})`);
      }
      if (!/^[\d\s.+\-*/().]+$/.test(expr)) {
        throw new Error('invalid formula');
      }
      // eslint-disable-next-line no-new-func
      const result = Function(`"use strict"; return (${expr});`)();
      if (typeof result !== 'number' || !Number.isFinite(result)) {
        throw new Error('non-numeric');
      }
      return result;
    },
    formatDate(iso) {
      return iso ? dayjs(iso).format('YYYY-MM-DD HH:mm') : '';
    },
    statusLabel(report) {
      const map = {
        queued: this.$t('reports.status_queued'),
        calculating: this.$t('reports.status_calculating'),
        ready: this.$t('reports.status_ready'),
        failed: this.$t('reports.status_failed'),
      };
      return map[report.status] || report.status;
    },
    statusColor(report) {
      if (report.status === 'ready') return 'success';
      if (report.status === 'failed') return 'error';
      if (report.status === 'calculating') return 'primary';
      return 'grey';
    },
  },
};
</script>

<style scoped>
.report-monthly-table-wrap {
  overflow-x: auto;
}
.report-monthly-table :deep(th),
.report-monthly-table :deep(td) {
  white-space: nowrap;
}
</style>
